export function cn(...inputs) {
  return inputs.flat(Infinity).filter(Boolean).join(" ");
}

// Module-level "current user timezone". Defaults to EST.
// AuthContext writes the logged-in user's preference into this via
// `setAppTimezone(...)` on every load — so date formatters everywhere
// pick it up without having to thread `tz` through every component.
let _appTimezone = "America/New_York";
export function setAppTimezone(tz) {
  if (tz && typeof tz === "string") _appTimezone = tz;
}
export function getAppTimezone() {
  return _appTimezone;
}

// Best-guess of the browser/device timezone, e.g. "America/Chicago".
// Used at login to auto-detect a sensible default the first time.
export function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: _appTimezone,
      timeZoneName: "short",
    });
  } catch {
    return String(d);
  }
}

export function fmtDateShort(d) {
  if (!d) return "";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      timeZone: _appTimezone,
    });
  } catch {
    return String(d);
  }
}

export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// --- Watermark helpers -------------------------------------------------------
//
// Stamp the Literally Spirits logo + a short date label onto a user photo
// before uploading. Returns a base64 JPEG data URL.
//
// Constraints:
//   • Max long-edge resized to 1600px (keeps payload well under 10MB).
//   • Watermark sits in bottom-right with a translucent dark pill behind it.
//
// If anything fails (e.g. logo can't load, decode error), the function falls
// back to returning the original file as base64 — so visit logging is never
// blocked by the watermark step.

let _logoPromise = null;
function loadLogo() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = "/literally-logo.png";
  });
  return _logoPromise;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function watermarkImage(file, dateLabel) {
  try {
    const [img, logo] = await Promise.all([loadImageFromFile(file), loadLogo()]);
    const MAX = 1600;
    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
    const W = Math.round(img.width * scale);
    const H = Math.round(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, W, H);

    // Compute watermark sizing relative to image dimensions.
    const pad = Math.max(12, Math.round(Math.min(W, H) * 0.02));
    const logoSize = Math.max(28, Math.min(56, Math.round(Math.min(W, H) * 0.07)));
    const fontPx = Math.max(11, Math.round(logoSize * 0.32));
    const label = (dateLabel || new Date().toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    })).toUpperCase();
    const text = `LITERALLY SPIRITS · ${label}`;

    ctx.font = `600 ${fontPx}px Manrope, system-ui, sans-serif`;
    const textW = ctx.measureText(text).width;
    const innerGap = Math.round(logoSize * 0.35);
    const pillH = logoSize + Math.round(logoSize * 0.3);
    const pillW = logoSize + innerGap + textW + Math.round(logoSize * 0.55);
    const pillX = W - pillW - pad;
    const pillY = H - pillH - pad;
    const radius = pillH / 2;

    // Translucent dark pill behind the watermark.
    ctx.fillStyle = "rgba(20, 16, 12, 0.55)";
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") {
      ctx.roundRect(pillX, pillY, pillW, pillH, radius);
    } else {
      // Fallback for older browsers.
      ctx.moveTo(pillX + radius, pillY);
      ctx.lineTo(pillX + pillW - radius, pillY);
      ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + radius);
      ctx.lineTo(pillX + pillW, pillY + pillH - radius);
      ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - radius, pillY + pillH);
      ctx.lineTo(pillX + radius, pillY + pillH);
      ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - radius);
      ctx.lineTo(pillX, pillY + radius);
      ctx.quadraticCurveTo(pillX, pillY, pillX + radius, pillY);
    }
    ctx.fill();

    // Logo (circular crop using clip path).
    const logoX = pillX + Math.round(logoSize * 0.15);
    const logoY = pillY + (pillH - logoSize) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
    ctx.restore();

    // Text.
    ctx.fillStyle = "#FFFFFF";
    ctx.textBaseline = "middle";
    ctx.fillText(text, logoX + logoSize + innerGap, pillY + pillH / 2 + 1);

    return canvas.toDataURL("image/jpeg", 0.88);
  } catch (_err) {
    // Watermarking failed — fall back to original photo so the user isn't blocked.
    return fileToBase64(file);
  }
}
